import type { OrderDetail } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatDateTime } from '@/shared/lib';
import { ContentCard } from '@/shared/ui';

interface OrderHistoryProps {
  readonly order: OrderDetail;
}

const KINDS = { CUSTOMER: '고객 취소', SUPPLY_FAILURE: '공급 불가' } as const;
const STATUSES = { REQUESTED: '처리 중', ACCEPTED: '환불 완료', REJECTED: '반려' } as const;
const ITEM_CLASS = 'border-t border-foreground/[0.06] pt-3 first:border-t-0 first:pt-0';
const META_CLASS = 'mt-1 text-xs text-muted-foreground';
const EMPTY_CLASS = 'text-sm text-muted-foreground';

/**
 * 처리 이력 — 지연 안내(이전→새 출고 예정일·사유·안내한 사람)와 취소 요청(종류·처리 상태·사유·환급 기한·처리 결과).
 * 사람은 서버가 푼 표시 이름으로 보이고, 주문한 고객 본인이 요청한 취소는 '고객'이다.
 */
export const OrderHistory = ({ order }: OrderHistoryProps): ReactElement => (
  <>
    <ContentCard title="지연 안내 이력">
      {order.delayNotices.length === 0 ? (
        <p className={EMPTY_CLASS}>지연 안내 없음</p>
      ) : (
        <ul className="flex flex-col gap-3 text-sm">
          {order.delayNotices.map((notice) => (
            <li key={notice.id} className={ITEM_CLASS}>
              <p className="font-medium">
                {notice.previousDate ?? '미정'} → {notice.newDate}
              </p>
              <p className="mt-1 whitespace-pre-wrap">{notice.reason}</p>
              <p className={META_CLASS}>
                {formatDateTime(notice.createdAt)} · 안내한 사람 {notice.createdByName}
              </p>
            </li>
          ))}
        </ul>
      )}
    </ContentCard>

    <ContentCard title="취소 요청 이력">
      {order.cancelRequests.length === 0 ? (
        <p className={EMPTY_CLASS}>취소 요청 없음</p>
      ) : (
        <ul className="flex flex-col gap-3 text-sm">
          {order.cancelRequests.map((request) => (
            <li key={request.id} className={ITEM_CLASS}>
              <p className="font-medium whitespace-pre-wrap">
                {`${KINDS[request.kind]} · ${STATUSES[request.status]} · ${request.reason}`}
              </p>
              <p className={META_CLASS}>
                요청 {formatDateTime(request.requestedAt)} · 요청자{' '}
                {request.requestedBy === order.userId ? '고객' : request.requestedByName} · 환급
                기한 {request.refundDueDate}
              </p>
              {request.rejectReason ? (
                <p className="mt-1 whitespace-pre-wrap">반려 사유: {request.rejectReason}</p>
              ) : null}
              {request.resolvedAt ? (
                <p className={META_CLASS}>
                  처리 {formatDateTime(request.resolvedAt)} · 처리자{' '}
                  {request.resolvedByName ?? '결제 결과 자동 반영'}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </ContentCard>
  </>
);
